import {
  AgxColumn,
  AgxDropdown,
  AgxSaveChangesButton,
  DropdownOption,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2';
import { AgxToast } from '@urbanx/agx-ui-components';
import LoadingState from 'components/loadingState/LoadingState';
import { useEffect, useState } from 'react';
import TeamPageHeader from '../TeamPageHeader/TeamPageHeader';
import { TeamPageTabs } from '../TeamPageHeader/TeamPageTabs';
import {
  StaffModel,
  useGetStaffByRoles,
  useInvalidateUseGetAllStaff,
} from '@urbanx/agx.staff.client';
import {
  useGetUrbanXTeamForAgency,
  useInvalidateUseGetUrbanXTeamForAgency,
  useInvalidateUseGetStaffMembersAssignedToAgencies,
  useSetUrbanXTeamForAgency,
  UrbanXTeam,
  UrbanXTeamDto,
} from '@urbanx/agx.agencies.client';
import useAgencyIdParam from 'hooks/useAgencyIdParam';
import Panel from 'components/panel/Panel';
import { StaffRole } from '@urbanx/agx.auth.client';
import { isDirtyEqual } from 'utils/compares';
import { AgxToastState } from 'types/commonTypes';

const defaultOption: DropdownOption = { value: '', label: 'None' };

const mapUrbanXTeamDtoToForm = (urbanXTeamForAgency?: UrbanXTeamDto): UrbanXTeam => ({
  businessManagerId: urbanXTeamForAgency?.businessManager?.email ?? '',
  adminManagerId: urbanXTeamForAgency?.adminManager?.email ?? '',
  marketingExpertId: urbanXTeamForAgency?.marketingExpert?.email ?? '',
});

const mapUrbanXTeamToForm = (urbanXTeam?: UrbanXTeam): UrbanXTeam => ({
  businessManagerId: urbanXTeam?.businessManagerId ?? '',
  adminManagerId: urbanXTeam?.adminManagerId ?? '',
  marketingExpertId: urbanXTeam?.marketingExpertId ?? '',
});

const GetStaffOptionsByRole = (role: StaffRole, staffMembers: StaffModel[]): DropdownOption[] => {
  if (!staffMembers) return [];

  const staffMembersForRole = staffMembers.filter((s) => s.roles?.includes(role));

  const options = staffMembersForRole.map((s) => ({
    value: s.email ?? '',
    label: `${s.firstName} ${s.lastName}`,
  }));

  return [defaultOption, ...options];
};

const ManageUrbanXTeamPage = () => {
  const agencyId = useAgencyIdParam();

  const { data: urbanXTeamForAgency, isPending: isUrbanXTeamForAgencyPending } =
    useGetUrbanXTeamForAgency(agencyId ?? '', undefined, !!agencyId);
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency();
  const invalidateGetAllStaff = useInvalidateUseGetAllStaff();
  const invalidateGetStaffMembersAssignedToAgencies =
    useInvalidateUseGetStaffMembersAssignedToAgencies();

  const { data: staffByRoles, isPending: isRolesPending } = useGetStaffByRoles([
    StaffRole.BusinessManager,
    StaffRole.AdminManager,
    StaffRole.MarketingExpert,
  ]);

  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  );
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const { setUrbanXTeamForAgency, setUrbanXTeamForAgencyPending } = useSetUrbanXTeamForAgency({
    onSuccess: async (_, inputData) => {
      setInitialForm(mapUrbanXTeamToForm(inputData.urbanXTeam));
      setSaveButtonState(SAVE_BUTTON_STATES.SAVED);
      updateToastState({
        color: 'success',
        message: 'Changes Saved',
        open: true,
      });

      const currentAgencyId = inputData.agencyId;
      await Promise.all([
        invalidateGetAllStaff(),
        ...(currentAgencyId
          ? [
              invalidateUrbanXTeamForAgency(currentAgencyId),
              ...[StaffRole.BusinessManager, StaffRole.AdminManager, StaffRole.MarketingExpert].map(
                (role) => invalidateGetStaffMembersAssignedToAgencies(role, [currentAgencyId])
              ),
            ]
          : []),
      ]);
    },
    onError: (error) => {
      console.error('Failed to set UrbanX team:', error);
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      updateToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const [form, updateForm] = useState<UrbanXTeam>(() =>
    mapUrbanXTeamDtoToForm(urbanXTeamForAgency)
  );
  const [initialForm, setInitialForm] = useState<UrbanXTeam>(() =>
    mapUrbanXTeamDtoToForm(urbanXTeamForAgency)
  );

  useEffect(() => {
    if (!urbanXTeamForAgency) return;

    const mappedForm = mapUrbanXTeamDtoToForm(urbanXTeamForAgency);
    updateForm(mappedForm);
    setInitialForm(mappedForm);
  }, [urbanXTeamForAgency]);

  const isFormDirty = !isDirtyEqual(form, initialForm);

  const saveChangesState = setUrbanXTeamForAgencyPending
    ? SAVE_BUTTON_STATES.SAVING
    : saveButtonState === SAVE_BUTTON_STATES.ERROR
      ? SAVE_BUTTON_STATES.ERROR
      : isFormDirty
        ? SAVE_BUTTON_STATES.UNSAVED
        : saveButtonState;

  useEffect(() => {
    if (isFormDirty) {
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
    }
  }, [isFormDirty]);

  const isAnythingLoading = isUrbanXTeamForAgencyPending || isRolesPending;

  const businessManagers = GetStaffOptionsByRole(StaffRole.BusinessManager, staffByRoles ?? []);
  const adminManagers = GetStaffOptionsByRole(StaffRole.AdminManager, staffByRoles ?? []);
  const marketingExperts = GetStaffOptionsByRole(StaffRole.MarketingExpert, staffByRoles ?? []);

  const onSaveClicked = async () => {
    if (!form || !isFormDirty || setUrbanXTeamForAgencyPending) return;

    await setUrbanXTeamForAgency({ agencyId, urbanXTeam: form });
  };

  return (
    <AgxColumn fill gap="spacing-xl">
      <AgxToast selector="#agxToast" toastState={toastState} />

      <TeamPageHeader
        selectedTab={TeamPageTabs.UrbanXTeam}
        actionButtons={
          <AgxSaveChangesButton
            state={saveChangesState}
            id={'saveUrbanXTeamButton'}
            text={'Save Changes'}
            variant={'primary'}
            onClick={onSaveClicked}
            disabled={!isFormDirty || setUrbanXTeamForAgencyPending}
          />
        }
      />

      {!isAnythingLoading && (
        <Panel>
          <AgxColumn fill gap="spacing-3xl">
            <AgxDropdown
              id="bm-select"
              hideOptionalText
              label="Business Manager"
              options={businessManagers}
              selected={form?.businessManagerId ?? ''}
              onChange={(value) => updateForm((prev) => ({ ...prev, businessManagerId: value }))}
            />

            <AgxDropdown
              id="am-select"
              hideOptionalText
              label="Default Admin Manager"
              hintText="Used for campaigns unless the lead agent's team has its own Admin Manager. Agents in more than one team always use the Default Admin Manager."
              options={adminManagers}
              selected={form?.adminManagerId ?? ''}
              onChange={(value) => updateForm((prev) => ({ ...prev, adminManagerId: value }))}
            />

            <AgxDropdown
              id="me-select"
              hideOptionalText
              label="Marketing Expert"
              options={marketingExperts}
              selected={form?.marketingExpertId ?? ''}
              onChange={(value) => updateForm((prev) => ({ ...prev, marketingExpertId: value }))}
            />
          </AgxColumn>
        </Panel>
      )}

      {isAnythingLoading && <LoadingState />}
    </AgxColumn>
  );
};

export default ManageUrbanXTeamPage;
